// GPS — wraps the browser Geolocation API
// Returns a reactive position that updates as the user moves.

import { useState, useEffect, useRef } from 'react';

export interface GpsPosition {
  lat: number;
  lng: number;
  accuracy: number;  // meters
  heading?: number;  // degrees, may be null
  speed?: number;    // m/s, may be null
  timestamp: number;
}

export interface GpsState {
  position: GpsPosition | null;
  error: string | null;
  watching: boolean;
}

export function useGps(): GpsState {
  const [state, setState] = useState<GpsState>({
    position: null,
    error: null,
    watching: false,
  });
  const watchId = useRef<number | null>(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setState(s => ({ ...s, error: 'Geolocation not supported' }));
      return;
    }

    setState(s => ({ ...s, watching: true }));

    const onSuccess = (pos: GeolocationPosition) => {
      setState({
        watching: true,
        error: null,
        position: {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading ?? undefined,
          speed: pos.coords.speed ?? undefined,
          timestamp: pos.timestamp,
        },
      });
    };

    const onError = (err: GeolocationPositionError) => {
      setState(s => ({ ...s, error: err.message }));
    };

    watchId.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 10000,
    });

    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, []);

  return state;
}

export async function getCurrentPosition(): Promise<GpsPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        heading: pos.coords.heading ?? undefined,
        speed: pos.coords.speed ?? undefined,
        timestamp: pos.timestamp,
      }),
      err => reject(new Error(err.message)),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  });
}
